import type { AeEmirateGroup } from './emirates';

/**
 * UAE delivery-promise windows (Asia/Dubai, UTC+4, no DST). Pure module, no
 * React — mirrors getIstParts in ../DeliveryEtaBadge/useSddNddEligibility.ts
 * but is purely frontend: no pincode, no serviceability API.
 *
 * Windows per emirate group (ops sheet, Oct 2026):
 *
 *   dubai              00:00–09:59 same_day · 10:00–16:59 four_hour · 17:00– next_day
 *   abu_dhabi_sharjah  00:00–09:59 same_day · 10:00–12:59 four_hour · 13:00– next_day
 *   ineligible         same boundaries as abu_dhabi_sharjah; the copy layer
 *                      (./copy.ts) ignores the window for this group and
 *                      always shows the flat next-day promise — eligibility
 *                      is a property of the emirate, not the time of day.
 */
export type AeDeliveryWindow = 'same_day' | 'four_hour' | 'next_day';

/** Window plus the TSV error row: timezone lookup threw (exotic browsers
 * without Asia/Dubai Intl data). Produced by useAeDeliveryWindow, never by
 * the pure resolver. */
export type AeDeliveryWindowState = AeDeliveryWindow | 'error';

/** Minutes-of-day boundaries, Asia/Dubai. sameDayEnd inclusive starts
 * four_hour; fourHourEnd inclusive starts next_day. */
export const AE_WINDOW_BOUNDS: Record<
  AeEmirateGroup,
  { sameDayEnd: number; fourHourEnd: number }
> = {
  dubai: { sameDayEnd: 10 * 60, fourHourEnd: 17 * 60 },
  abu_dhabi_sharjah: { sameDayEnd: 10 * 60, fourHourEnd: 13 * 60 },
  ineligible: { sameDayEnd: 10 * 60, fourHourEnd: 13 * 60 },
};

type DubaiParts = { hour: number; minute: number; second: number };

// Module-level singleton — constructing Intl.DateTimeFormat is comparatively
// expensive and this runs on every render tick / timer sync.
const dubaiTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Dubai',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const getDubaiParts = (date: Date): DubaiParts => {
  const parts = dubaiTimeFormatter.formatToParts(date);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? '0';
  return {
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
};

export const resolveAeDeliveryWindow = (
  group: AeEmirateGroup,
  now: Date = new Date(),
): AeDeliveryWindow => {
  const { hour, minute } = getDubaiParts(now);
  const minutesOfDay = hour * 60 + minute;
  const bounds = AE_WINDOW_BOUNDS[group];
  if (minutesOfDay < bounds.sameDayEnd) return 'same_day';
  if (minutesOfDay < bounds.fourHourEnd) return 'four_hour';
  return 'next_day';
};

/** Union of every group's boundaries plus midnight, in minutes-of-day —
 * DERIVED from AE_WINDOW_BOUNDS so an ops cutoff change can never leave the
 * timer schedule behind. Group-agnostic so a timer armed on it never needs
 * re-arming when the user switches emirate; a tick at a boundary the current
 * group doesn't use re-resolves to the same window and React bails out on
 * the identical string state. */
const AE_BOUNDARY_MINUTES = [
  ...new Set(
    Object.values(AE_WINDOW_BOUNDS).flatMap((bounds) => [
      bounds.sameDayEnd,
      bounds.fourHourEnd,
    ]),
  ),
  24 * 60,
].sort((a, b) => a - b);

/** Milliseconds until the next boundary, so a mounted component can
 * re-render exactly when the window flips instead of polling. +50ms guards
 * against firing a tick early from timer jitter. */
export const msUntilNextAeBoundary = (now: Date = new Date()): number => {
  const { hour, minute, second } = getDubaiParts(now);
  const secondsOfDay = hour * 3600 + minute * 60 + second;
  const nextBoundarySeconds =
    AE_BOUNDARY_MINUTES.map((m) => m * 60).find(
      (boundary) => boundary > secondsOfDay,
    ) ?? 24 * 3600;
  const msUntil =
    (nextBoundarySeconds - secondsOfDay) * 1000 - now.getMilliseconds() + 50;
  return Math.max(msUntil, 0);
};
